package com.htmlagent.site.state.chat

import com.htmlagent.shared.UiLocale
import com.htmlagent.shared.session.SessionUsageResponse
import kotlin.js.json

private const val MICROS_PER_UNIT = 1_000_000.0
private const val THOUSAND = 1_000.0

/**
 * How consumption is painted.
 *
 * The number is formatted by the browser with the interface's locale and not by hand: a decimal
 * comma and a thousands separator change between Spanish and English, and a figure about money
 * that is punctuated wrong reads as a different figure.
 */
object UsageFormat {

    /** `12,4k` past a thousand, the exact count below it: `948` is not `0,9k`. */
    fun tokens(total: Int, locale: UiLocale): String =
        if (total < THOUSAND) {
            decimal(total.toDouble(), locale, digits = 0)
        } else {
            decimal(total / THOUSAND, locale, digits = 1) + "k"
        }

    /**
     * The amount with its symbol, as a currency.
     *
     * Four decimals and not two: a turn costs thousandths, and rounding to cents would show
     * every session at `0,00` - a figure that says the product is free, which is the one thing
     * this counter exists to disprove.
     */
    fun cost(usage: SessionUsageResponse, locale: UiLocale): String =
        (usage.costMicros / MICROS_PER_UNIT).asDynamic().toLocaleString(
            locale.tag,
            json(
                "style" to "currency",
                "currency" to usage.currency,
                "minimumFractionDigits" to 2,
                "maximumFractionDigits" to COST_DIGITS,
            ),
        ) as String

    private fun decimal(value: Double, locale: UiLocale, digits: Int): String =
        value.asDynamic().toLocaleString(
            locale.tag,
            json("minimumFractionDigits" to 0, "maximumFractionDigits" to digits),
        ) as String

    /** Enough to tell a cheap edit from a full generation; two would show both as zero. */
    private const val COST_DIGITS = 4
}
